import { useState, useEffect, useRef } from 'react'
import { fetchMagnumMoment } from '@/lib/magnumMoment'
import { timArt, randomLaunchArt } from '@/lib/timArt'
import { useMagnum } from '@/context/MagnumContext'

interface Props {
  minutes: number
  tvName: string
  /** Performs the redemption. Resolves on success, throws with a message on failure. */
  onLaunch: () => Promise<void>
  onClose: () => void
}

/** Shown if the engine returns nothing — the bubble must never be empty here. */
const ARMED_FALLBACK = 'I am ready to serve.'
const LAUNCH_FALLBACK = 'I AM BECOME SCREEN TIME.'

type Phase = 'armed' | 'launching' | 'launched' | 'failed'

export default function ScreenTimeLaunchModal({ minutes, tvName, onLaunch, onClose }: Props) {
  const { claimTakeover } = useMagnum()
  const [phase, setPhase] = useState<Phase>('armed')
  const [quip, setQuip] = useState(ARMED_FALLBACK)
  const [error, setError] = useState<string | null>(null)
  // Picked once on mount so it can preload while the kid reads the armed line.
  const launchImage = useRef(randomLaunchArt())

  useEffect(() => {
    const img = new Image()
    img.src = launchImage.current
  }, [])

  // This modal is Tim for as long as it is open.
  useEffect(() => claimTakeover(), [claimTakeover])

  useEffect(() => {
    fetchMagnumMoment({
      contextType: 'SCREEN_TIME_ARMED',
      streak: 0,
      facets: { minutes: String(minutes) },
    })
      .then((m) => { if (m.line?.text) setQuip(m.line.text) })
      .catch(() => {})
  }, [minutes])

  const handleLaunch = async () => {
    if (phase !== 'armed') return
    setPhase('launching')
    setError(null)
    try {
      await onLaunch()
    } catch (err) {
      setPhase('failed')
      setError(err instanceof Error ? err.message : "That didn't work — try again.")
      return
    }

    setQuip(LAUNCH_FALLBACK)
    setPhase('launched')
    fetchMagnumMoment({
      contextType: 'SCREEN_TIME_LAUNCH',
      streak: 0,
      facets: { minutes: String(minutes) },
    })
      .then((m) => { if (m.line?.text) setQuip(m.line.text) })
      .catch(() => {})
  }

  // Let the spectacle land, then get out of the way.
  useEffect(() => {
    if (phase !== 'launched') return
    const t = setTimeout(onClose, 6000)
    return () => clearTimeout(t)
  }, [phase, onClose])

  const launched = phase === 'launched'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 animate-in fade-in duration-200"
      onClick={launched ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Start screen time"
    >
      <div
        className={`relative w-full max-w-sm overflow-hidden rounded-3xl border-2 shadow-2xl transition-colors duration-500 ${
          launched ? 'border-cyan-300 bg-slate-950' : 'border-primary/40 bg-card'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {launched ? (
          <div className="relative">
            <img
              src={launchImage.current}
              alt="Tim opening the stream"
              className="w-full object-cover animate-in zoom-in-95 duration-500"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/90 to-transparent p-5 pt-16">
              <p className="text-center text-xl font-black leading-tight text-cyan-200 drop-shadow">
                {quip}
              </p>
              <p className="mt-3 text-center text-sm font-semibold text-cyan-100/80">
                {minutes} minutes on {tvName}. Go.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5 p-6">
            <div className="flex items-end gap-1">
              <img
                src={timArt('happy')}
                alt="Tim"
                className="h-20 w-20 shrink-0 object-contain"
              />
              <div className="relative flex-1">
                <div className="rounded-2xl rounded-bl-none border-2 border-primary/30 bg-primary/5 px-4 py-2.5">
                  <p className="text-sm font-medium">{quip}</p>
                </div>
                <div className="absolute bottom-0 -left-[8px] h-0 w-0 border-t-[8px] border-r-[8px] border-t-transparent border-r-primary/30" />
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              <span className="font-bold text-foreground">{minutes} minutes</span> on {tvName}
            </p>

            <button
              onClick={handleLaunch}
              disabled={phase === 'launching'}
              aria-label={`Start ${minutes} minutes of screen time`}
              className="group relative mx-auto flex h-32 w-32 items-center justify-center rounded-full border-[6px] border-red-900 bg-gradient-to-b from-red-500 to-red-700 shadow-[0_10px_0_0_theme(colors.red.900),0_14px_24px_rgba(0,0,0,0.45)] transition-all duration-100 hover:from-red-400 hover:to-red-600 active:translate-y-[8px] active:shadow-[0_2px_0_0_theme(colors.red.900),0_4px_10px_rgba(0,0,0,0.4)] disabled:opacity-70 motion-safe:animate-pulse"
            >
              <span className="text-center text-lg font-black uppercase leading-none tracking-wide text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.5)]">
                {phase === 'launching' ? '...' : 'Start'}
              </span>
            </button>

            {error && (
              <p className="text-center text-sm font-medium text-red-600">{error}</p>
            )}

            <button
              onClick={onClose}
              className="mx-auto block min-h-[44px] px-4 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              Not yet
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
